import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';

type Scene = {
  title: string;
  subtitle: string;
  img: string;
  season: 'Winter' | 'Year-round' | 'Summer';
  months: string;
  alt: string;
  temp: string;
  live: number;
  motif: string;
  accent: string;
  to: string;
};

const SCENES: Scene[] = [
  {
    title: 'Above the snow line.',
    subtitle: 'Gulmarg · 13,800 ft',
    img: photo.snowPeaks,
    season: 'Winter',
    months: 'DEC—MAR',
    alt: '13,800 ft',
    temp: '-4°C',
    live: 42,
    motif: '❄',
    accent: brand.teal,
    to: '/search?season=winter',
  },
  {
    title: 'Where the lake breathes.',
    subtitle: 'Dal Lake · Srinagar',
    img: photo.reflection,
    season: 'Year-round',
    months: 'MAY—OCT',
    alt: '5,200 ft',
    temp: '18°C',
    live: 84,
    motif: '◈',
    accent: brand.ice,
    to: '/houseboats',
  },
  {
    title: 'Seven days, no signal.',
    subtitle: 'Great Lakes Trek',
    img: photo.alpineRidge,
    season: 'Summer',
    months: 'JUN—SEP',
    alt: '12,400 ft',
    temp: '11°C',
    live: 38,
    motif: '▲',
    accent: brand.saffron,
    to: '/search?type=trek',
  },
];

export function ThreeScenes() {
  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <SectionHeader
        kicker="Three states of Kashmir"
        title={
          <>
            Same valley. <em className="text-[var(--color-teal)]">Three impossible worlds.</em>
          </>
        }
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/search">
              Browse all
              <ArrowRight size={14} />
            </Link>
          </Button>
        }
      />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={inViewOnce}
        variants={staggerContainer}
        className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3"
      >
        {SCENES.map((s, i) => (
          <motion.div key={s.title} variants={fadeUp}>
            <Link
              to={s.to}
              className="group block h-full overflow-hidden rounded-[20px] border border-[var(--glass-edge)] glass-hi"
              style={{ boxShadow: `0 12px 40px ${s.accent}22` }}
            >
              {/* Top — image */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={unsplash(s.img, { w: 900 })}
                  alt={s.title}
                  className="absolute inset-0 h-full w-full object-cover saturate-110 transition-transform duration-700 group-hover:scale-[1.08]"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: `linear-gradient(180deg, transparent 50%, ${s.accent}1f)` }}
                />

                {/* index */}
                <div className="absolute left-4 top-3 flex items-baseline gap-1.5 text-white">
                  <span
                    className="font-display text-3xl font-semibold leading-none"
                    style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
                  >
                    0{i + 1}
                  </span>
                  <span className="text-[9px] font-bold uppercase tracking-[0.15em] opacity-80">
                    / 03
                  </span>
                </div>

                {/* motif */}
                <div className="absolute right-4 top-3 grid h-8 w-8 place-items-center rounded-full border border-white/30 text-base text-white backdrop-blur-md" style={{ background: 'rgba(255,255,255,0.18)' }}>
                  {s.motif}
                </div>

                {/* season + months */}
                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                  <span
                    className="rounded px-2 py-1 text-[9px] font-bold uppercase tracking-[0.15em] backdrop-blur-sm"
                    style={{ background: `${s.accent}cc` }}
                  >
                    {s.season}
                  </span>
                  <span className="text-[10px] font-semibold tracking-[0.1em] text-white/85">
                    {s.months}
                  </span>
                </div>
              </div>

              {/* Bottom — info */}
              <div className="flex flex-col gap-3 p-4">
                <div>
                  <h3 className="font-display text-lg font-semibold leading-tight tracking-tight text-ink">
                    {s.title}
                  </h3>
                  <div className="mt-1 text-[11px] font-medium text-ink-mute">{s.subtitle}</div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="border-l-2 pl-2" style={{ borderColor: s.accent }}>
                    <div className="text-[9px] font-semibold uppercase tracking-[0.1em] text-ink-mute">
                      Alt
                    </div>
                    <div className="text-[13px] font-semibold text-ink">{s.alt}</div>
                  </div>
                  <div className="border-l border-[var(--glass-edge)] pl-2">
                    <div className="text-[9px] font-semibold uppercase tracking-[0.1em] text-ink-mute">
                      Temp
                    </div>
                    <div className="text-[13px] font-semibold text-ink">{s.temp}</div>
                  </div>
                  <div className="border-l border-[var(--glass-edge)] pl-2">
                    <div className="text-[9px] font-semibold uppercase tracking-[0.1em] text-ink-mute">
                      Live
                    </div>
                    <div className="text-[13px] font-bold" style={{ color: s.accent }}>
                      {s.live}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
